import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from "react";
import { Col, Form, Row } from "react-bootstrap";

import { deepEqual, toDateOrNull } from "../../../utils/object";
import Admissions from "./admissions";

const normalizeAdmission = (item = {}) => ({
    uuid: item?.uuid ?? "",
    _cid: item?._cid ?? "",
    jurisdiction: item?.jurisdiction ?? "",
    courtName: item?.courtName ?? "",
    admissionType: item?.admissionType ?? "state_bar",
    barNumber: item?.barNumber ?? "",
    admissionDate: toDateOrNull(item?.admissionDate),
    status: item?.status ?? "active",
    isPrimary: Boolean(item?.isPrimary),
    meta:
        item?.meta && typeof item.meta === "object" && !Array.isArray(item.meta)
            ? item.meta
            : {},
});

const mapPropsToState = (props) => ({
    firmName: props.firmName ?? "",
    title: props.title ?? "",
    Admissions: Array.isArray(props.Admissions)
        ? props.Admissions.map(normalizeAdmission)
        : [],
});

const Attorney = forwardRef(function Attorney(props, ref) {
    const mappedProps = useMemo(
        () => mapPropsToState(props),
        [props.firmName, props.title, props.Admissions]
    );

    const [attorney, setAttorney] = useState(mappedProps);

    const isHydratingRef = useRef(true);
    const prevMappedRef = useRef(mappedProps);
    const initialValueRef = useRef(mappedProps);

    useEffect(() => {
        isHydratingRef.current = false;
    }, []);

    useEffect(() => {
        if (deepEqual(prevMappedRef.current, mappedProps)) return;

        isHydratingRef.current = true;
        setAttorney(mappedProps);
        prevMappedRef.current = mappedProps;
        initialValueRef.current = mappedProps;

        queueMicrotask(() => {
            isHydratingRef.current = false;
        });
    }, [mappedProps]);

    const updateField = useCallback((name, value) => {
        if (!name) return;
        setAttorney((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const onChange = useCallback(
        (e) => {
            const name = e?.target?.name;
            const value = e?.target?.value;
            updateField(name, value);
        },
        [updateField]
    );

    const onAdmissionsChange = useCallback(
        (Admissions) => {
            updateField(
                "Admissions",
                Array.isArray(Admissions) ? Admissions.map(normalizeAdmission) : []
            );
        },
        [updateField]
    );

    const validate = useCallback(() => {
        const errors = {};

        if (!String(attorney.title ?? "").trim()) {
            errors.title = "Title is required.";
        }

        const admissions = Array.isArray(attorney.Admissions) ? attorney.Admissions : [];
        const primaryCount = admissions.filter((item) => Boolean(item?.isPrimary)).length;

        if (admissions.length > 0 && primaryCount === 0) {
            errors.Admissions = "One admission should be marked as primary.";
        }

        if (primaryCount > 1) {
            errors.Admissions = "Only one admission may be marked as primary.";
        }

        admissions.forEach((item, index) => {
            if (!String(item?.jurisdiction ?? "").trim()) {
                errors[`Admissions.${index}.jurisdiction`] = `Admission #${index + 1}: jurisdiction is required.`;
            }

            if (!String(item?.status ?? "").trim()) {
                errors[`Admissions.${index}.status`] = `Admission #${index + 1}: status is required.`;
            }

            if (!String(item?.admissionType ?? "").trim()) {
                errors[`Admissions.${index}.admissionType`] = `Admission #${index + 1}: type is required.`;
            }
        });

        return {
            success: Object.keys(errors).length === 0,
            errors,
        };
    }, [attorney]);

    useImperativeHandle(
        ref,
        () => ({
            getValue: () => ({
                ...attorney,
                Admissions: Array.isArray(attorney.Admissions)
                    ? attorney.Admissions.map(normalizeAdmission)
                    : [],
            }),
            setValue: (value = {}) => {
                const next = mapPropsToState(value);
                setAttorney(next);
            },
            reset: (value = null) => {
                const next = value ? mapPropsToState(value) : initialValueRef.current;
                setAttorney(next);
            },
            validate,
            isDirty: () => !deepEqual(initialValueRef.current, attorney),
        }),
        [attorney, validate]
    );

    return (
        <>
            <Col xs="12">
                <Row>
                    <Col lg="12">
                        <Row className="g-3">
                            <Col lg="12">
                                <h2 className="small-title">Attorney</h2>
                            </Col>
                        </Row>

                        <Row className="g-3">
                            <Col md="6">
                                <div className="mb-3 top-label">
                                    <Form.Control
                                        type="text"
                                        name="firmName"
                                        value={attorney.firmName}
                                        onChange={onChange}
                                    />
                                    <Form.Label>FIRM NAME</Form.Label>
                                </div>
                            </Col>

                            <Col md="6">
                                <div className="mb-3 top-label">
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={attorney.title}
                                        onChange={onChange}
                                    />
                                    <Form.Label>TITLE</Form.Label>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Admissions
                    value={attorney.Admissions}
                    onChange={onAdmissionsChange}
                />
            </Col>
        </>
    );
});

export default Attorney;